import { useCallback, useContext } from "react";

import { AmplitudeFlagKey, FormType, RecaptchaGuardMessage, TicketingFormCategory } from "@simplyk/common";
import { useSnackbar } from "notistack";
import { FieldValues, UseFormSetValue } from "react-hook-form";

import { AmplitudeEvents } from "../../../constants/amplitude";
import { FrontendFormContext } from "../../../contexts/FrontendFormContext";
import { FrontendTicketingContext } from "../../../contexts/FrontendTicketingContext";
import { useUserInputContext } from "../../../contexts/UserInputContext";
import { RecaptchaAction } from "../../../enums/recaptcha";
import { useFormV2Context } from "../../../features/FormV2/context/FormV2Context";
import { useIsFormV2 } from "../../../features/FormV2/hooks/useIsFormV2";
import { trpc } from "../../../helpers/trpc";
import { useAmplitude } from "../../../hooks/amplitude/useAmplitude";
import { useFormQueryParams } from "../../../hooks/useFormParams";
import { useRecaptchaRetry } from "../../../hooks/useRecaptchaRetry";
import { useTranslate } from "../../../hooks/useTranslate";
import { unknownPaymentErrorCode } from "../../PaymentElement/helper";
import { formatSubmitCommandData, SubmitCommandFormInput } from "../helper";
import { PaymentElementProps, PaymentSucceeded } from "../type";

import { useGetNewCopyContributionCanadaTipValue } from "./useGetNewCopyContributionCanadaTipValue";

import { useAmplitudeFeatureFlag } from "@/hooks/amplitude/useAmplitudeFeatureFlag";
import { TicketingPaymentInput } from "@/types/ticketing";

export interface PaymentFailed {
  error: { message: string; code: string };
}

export type SubmitCommandResponse = PaymentSucceeded | PaymentFailed;

export const useSubmitCommand = <T extends FieldValues>({ setValue }: Pick<PaymentElementProps<T>, "setValue">) => {
  const { mutateAsync: submitCommandMutation } = trpc.form_submitCommand.useMutation();
  const { mutateAsync: logCommandUserInteractionMutation } = trpc.form_logCommandUserInteraction.useMutation();

  const { displayedFormAmount, stripeRecurrenceInterval, commandId, organization, category } =
    useContext(FrontendFormContext);

  const formV2Context = useFormV2Context();

  const { userInputs } = useUserInputContext();
  const { emailDeliveryId, impactChainUtmSourceEmail, emailId } = useFormQueryParams();

  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();
  const { executeTrpc } = useRecaptchaRetry();
  const isFormV2 = useIsFormV2();
  const { fallBackToTicketingContainerOnFullTicketing, fallBackToTicketingContainerOnNoTicketSelected } =
    useContext(FrontendTicketingContext);
  const { logAmplitudeEvent } = useAmplitude();

  const newCopyContributionCanadaTip = useGetNewCopyContributionCanadaTipValue({ organization });

  const easierDonorRefundExperiment = useAmplitudeFeatureFlag({
    flagKey: AmplitudeFlagKey.EasierDonorRefund,
    userProperties: {},
    skip: !organization,
  });

  const handleSubmitCommand = async (data: SubmitCommandFormInput): Promise<SubmitCommandResponse> => {
    if (!organization) {
      return { error: { message: t("common", "unknown_error"), code: "NoOrganizationError" } };
    }

    const params = formatSubmitCommandData(data, { displayedFormAmount, stripeRecurrenceInterval, isFormV2 });
    const action = RecaptchaAction.SubmitCommand;
    const email = params.email;

    if (params.productBids) {
      await logCommandUserInteractionMutation({ commandId, payload: userInputs });
    }

    let experimentValues = {};
    if (formV2Context.type === FormType.DonationForm) {
      const { hasChangedDonationAmount } = formV2Context;
      if (!hasChangedDonationAmount) {
        logAmplitudeEvent(AmplitudeEvents.DefaultAmountUsed);
      }

      experimentValues = {
        ...(newCopyContributionCanadaTip.value && {
          newCopyContributionCanadaTipVariant: newCopyContributionCanadaTip.value,
        }),
      };
    }

    experimentValues = {
      ...experimentValues,
      ...(easierDonorRefundExperiment.value && {
        easierDonorRefundVariant: easierDonorRefundExperiment.value,
      }),
    };

    const response = await executeTrpc(action, email, submitCommandMutation, {
      ...params,
      id: commandId,
      purchasedFromEmailDeliveryId: emailDeliveryId || undefined,
      purchasedFromEmailId: emailId || undefined,
      impactChainUtmSourceEmail: impactChainUtmSourceEmail || undefined,
      experimentValues,
    });

    const responseData = response.data;
    const error = response.error;

    if (error?.message === RecaptchaGuardMessage) {
      return { error: { message: t("common", "recaptcha_error"), code: "RecaptchaFailed" } };
    }

    if (error || !responseData) {
      const code = error?.code || "unknown_error";

      const unknownGenericErrorTraduction = t("common", "unknown_error", { code: unknownPaymentErrorCode.other3.code });

      const displayedMessage =
        code === "confirm_payment_intent_failed"
          ? error?.message // We use the stripe error message directly for card errors
          : t("common", code as never) === code // If the code translation is the same as the code (meaning translation does not exist), we use the generic error
            ? unknownGenericErrorTraduction
            : t("common", code as never);

      handleFallbackOnPaymentError(error);
      return {
        error: {
          code,
          message: displayedMessage || t("common", "unknown_error", { code: unknownPaymentErrorCode.other3.code }),
        },
      };
    }
    return { data: responseData, paymentInput: data.paymentInput };
  };

  const handleFallbackOnPaymentError = useCallback(
    (error: { code?: string | null } | undefined | null) => {
      if (error) {
        if (error.code === "ticketing_is_full") {
          enqueueSnackbar(t("ticketing", "errorOnFullTicketing"), { vibe: "danger" });
          fallBackToTicketingContainerOnFullTicketing(setValue as UseFormSetValue<TicketingPaymentInput>);
          return;
        }
        if (error.code === "ticketing_is_closed") {
          const isAuction = category === TicketingFormCategory.Auction;
          enqueueSnackbar(t("ticketing", isAuction ? "errorOnClosedAuction" : "errorOnClosedOccurrence"), {
            vibe: "danger",
          });
          fallBackToTicketingContainerOnNoTicketSelected(setValue as UseFormSetValue<TicketingPaymentInput>);
          return;
        }
        if (error.code === "full_choice_errors") {
          enqueueSnackbar(t("ticketing", "errorOnFullChoice"), { vibe: "danger" });
          fallBackToTicketingContainerOnNoTicketSelected(setValue as UseFormSetValue<TicketingPaymentInput>);
          return;
        }
        if (error.code === "expired_early_bird") {
          enqueueSnackbar(t("ticketing", "errorOnExpiredEarlyBirdRate"), { vibe: "danger" });
          fallBackToTicketingContainerOnNoTicketSelected(setValue as UseFormSetValue<TicketingPaymentInput>);
          return;
        }
        if (error.code === "archived_rate_selected") {
          enqueueSnackbar(t("ticketing", "errorOnArchivedRateSelected"), { vibe: "danger" });
          fallBackToTicketingContainerOnNoTicketSelected(setValue as UseFormSetValue<TicketingPaymentInput>);
          return;
        }
        if (error.code === "form_deleted") {
          enqueueSnackbar(t("ticketing", "errorOnFormDeleted"), { vibe: "danger" });
          fallBackToTicketingContainerOnNoTicketSelected(setValue as UseFormSetValue<TicketingPaymentInput>);
          return;
        }
      }
    },
    [
      category,
      enqueueSnackbar,
      fallBackToTicketingContainerOnFullTicketing,
      fallBackToTicketingContainerOnNoTicketSelected,
      setValue,
      t,
    ]
  );

  return { handleSubmitCommand };
};
